import '@common/config/localization'

import React, { useEffect } from 'react'
import { SafeAreaProvider } from 'react-native-safe-area-context'
import { HashRouter } from 'react-router-dom'

import ErrorComponent from '@common/components/ErrorBoundary'
import { GlobalTooltip } from '@common/components/GlobalTooltip'
import { ErrorBoundary } from '@common/config/analytics/CrashAnalytics.web'
import { BiometricsProvider } from '@common/contexts/biometricsContext'
import { ConstantsProvider } from '@common/contexts/constantsContext'
import { ContractNamesContextProvider } from '@common/contexts/contractNamesContext'
import { ControllerStoreProvider } from '@common/contexts/controllerStoreContext'
import { NetInfoProvider } from '@common/contexts/netInfoContext'
import { ReceiveSheetProvider } from '@common/contexts/receiveSheetContext'
import { ThemeProvider } from '@common/contexts/themeContext'
import { ToastProvider } from '@common/contexts/toastContext'
import useFonts from '@common/hooks/useFonts'
import { dismissBootLoader } from '@common/utils/bootLoader'
import { AuthProvider } from '@common/modules/auth/contexts/authContext'
import { OnboardingNavigationProvider } from '@common/modules/auth/contexts/onboardingNavigationContext'
import GestureHandler from '@common/modules/app-init/screens/AppInit/GestureHandler'
import { PortalHost, PortalProvider } from '@gorhom/portal'
import { ControllersMiddlewareProvider } from '@mobile/contexts/controllersMiddlewareContext'
import { ControllersStateLoadedProvider } from '@mobile/contexts/controllersStateLoadedContext/controllersStateLoadedContext'
import MobileRouter from '@mobile/modules/router/Router'
import { BackgroundServiceProvider } from '@web/contexts/backgroundServiceContext'
import { AppModeContextProvider } from '@web/contexts/appModeContext'
import { RailgunContextProvider } from '@web/contexts/railgunContext'
import LatticePairingModal from './components/LatticePairingModal'

const errorComponent = ({ error }: { error: Error }) => {
  // Surface caught errors to console so devs can see what broke before
  // Sentry swallows them in the styled fallback view.
  // eslint-disable-next-line no-console
  console.error('[mobile-web ErrorBoundary]', error?.message, error?.stack)
  return <ErrorComponent error={error} />
}

const AppInit = () => {
  const { fontsLoaded, robotoFontsLoaded } = useFonts()

  // The inline boot loader (index.html #zkx-loader) is dismissed by the web
  // Router once the first route screen actually mounts — that covers the gap
  // while controllers load AND while lazy route chunks fetch (Suspense). This
  // is only a safety net so the loader can never stick if init stalls.
  useEffect(() => {
    // Boot progress milestone: React has mounted and AppInit is running.
    ;(window as any).__zkxBoot?.set?.(0.5)
    const timer = setTimeout(dismissBootLoader, 20000)
    return () => clearTimeout(timer)
  }, [])

  if (!fontsLoaded && !robotoFontsLoaded) return null

  return (
    <HashRouter>
      <PortalProvider>
        <GlobalTooltip />
        <SafeAreaProvider>
          <ToastProvider>
            <ConstantsProvider>
              <ErrorBoundary fallback={errorComponent as any}>
                <ControllerStoreProvider withErrorToasts>
                  <ControllersMiddlewareProvider>
                    <BackgroundServiceProvider>
                      <ThemeProvider>
                        <GestureHandler>
                          <ControllersStateLoadedProvider>
                            <NetInfoProvider>
                              <AuthProvider>
                                <BiometricsProvider>
                                  <OnboardingNavigationProvider>
                                    <AppModeContextProvider>
                                      <RailgunContextProvider>
                                        <ContractNamesContextProvider>
                                          <ReceiveSheetProvider>
                                            <MobileRouter />
                                            <LatticePairingModal />
                                          </ReceiveSheetProvider>
                                          <PortalHost name="global" />
                                        </ContractNamesContextProvider>
                                      </RailgunContextProvider>
                                    </AppModeContextProvider>
                                  </OnboardingNavigationProvider>
                                </BiometricsProvider>
                              </AuthProvider>
                            </NetInfoProvider>
                          </ControllersStateLoadedProvider>
                        </GestureHandler>
                      </ThemeProvider>
                    </BackgroundServiceProvider>
                  </ControllersMiddlewareProvider>
                </ControllerStoreProvider>
              </ErrorBoundary>
            </ConstantsProvider>
          </ToastProvider>
        </SafeAreaProvider>
      </PortalProvider>
    </HashRouter>
  )
}

export default AppInit
